import { Link } from 'react-router-dom';
import { Instagram, Mail, MapPin, Phone } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#414b3b] text-[#f5f3ee]" data-testid="footer">
      {/* Large Brand Name */}
      <div className="border-b border-[#f5f3ee]/10">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
          <h2 className="font-heading font-light text-5xl sm:text-7xl md:text-8xl text-center tracking-[0.1em] text-[#f5f3ee]/10">
            PADEL HOLIDAYS
          </h2>
        </div>
      </div>

      {/* Footer Content */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_elegant-padel-1/artifacts/zjm8foo8_Padel%20Holidays%20%281%29.png"
                alt="Padel Holidays Algarve"
                className="h-20 w-auto object-contain invert"
              />
            </div>
            <p className="text-[#f5f3ee]/50 text-sm font-light leading-relaxed mb-6">
              Curated padel travel experiences in the Algarve. Combining world-class sport with refined travel.
            </p>
            <div className="flex gap-4">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#f5f3ee]/20 flex items-center justify-center hover:border-[#8ca87c] hover:text-[#8ca87c] transition-all duration-300"
                data-testid="social-instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href="mailto:padelholidaysalgarve@gmail.com"
                className="w-10 h-10 border border-[#f5f3ee]/20 flex items-center justify-center hover:border-[#8ca87c] hover:text-[#8ca87c] transition-all duration-300"
                data-testid="social-email"
              >
                <Mail size={16} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-sm tracking-[0.2em] uppercase mb-6 text-[#8ca87c]">Navigation</h4>
            <ul className="space-y-3">
              {[
                { label: 'Home', path: '/' },
                { label: 'Destinations', path: '/destinations' },
                { label: 'Experiences', path: '/experiences' },
                { label: 'How It Works', path: '/how-it-works' },
                { label: 'About Us', path: '/about' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-[#f5f3ee]/50 hover:text-[#8ca87c] transition-colors duration-300 text-sm font-light"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Experiences */}
          <div>
            <h4 className="font-heading text-sm tracking-[0.2em] uppercase mb-6 text-[#8ca87c]">Experiences</h4>
            <ul className="space-y-3">
              {[
                'Weekend Escape',
                'Classic Week',
                'Intensive Camp',
                'Corporate Retreat',
              ].map((item) => (
                <li key={item}>
                  <Link
                    to="/experiences"
                    className="text-[#f5f3ee]/50 hover:text-[#8ca87c] transition-colors duration-300 text-sm font-light"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-sm tracking-[0.2em] uppercase mb-6 text-[#8ca87c]">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-[#8ca87c] mt-0.5 flex-shrink-0" />
                <span className="text-[#f5f3ee]/50 text-sm font-light">Algarve, Portugal</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-[#8ca87c] flex-shrink-0" />
                <a
                  href="mailto:padelholidaysalgarve@gmail.com"
                  className="text-[#f5f3ee]/50 hover:text-[#8ca87c] transition-colors duration-300 text-sm font-light"
                  data-testid="footer-email"
                >
                  padelholidaysalgarve@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-[#8ca87c] flex-shrink-0" />
                <a
                  href="tel:+351968382742"
                  className="text-[#f5f3ee]/50 hover:text-[#8ca87c] transition-colors duration-300 text-sm font-light"
                  data-testid="footer-phone"
                >
                  +351 968 382 742
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-[#f5f3ee]/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#f5f3ee]/30 text-xs font-light text-center md:text-left tracking-wider">
            © {currentYear} Padel Holidays Algarve. All rights reserved.
          </p>
          <div className="flex gap-8">
            <Link to="/privacy" className="text-[#f5f3ee]/30 hover:text-[#f5f3ee]/50 text-xs font-light transition-colors tracking-wider">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-[#f5f3ee]/30 hover:text-[#f5f3ee]/50 text-xs font-light transition-colors tracking-wider">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
